/**
 * VM-11b — Kündigungs-Dialog (Version 2).
 *
 * Erfasst: Kündigungspartei (Mieter/Vermieter), Kündigungsdatum (Ausstellung),
 * Eingangsdatum + Eingang bei (Verwalter/Eigentümer). Berechnet effektives
 * Vertragsende serverseitig unter Berücksichtigung von BGB §573c:
 *  - Zugang bis 3. Werktag im Monat → Frist beginnt in diesem Monat
 *  - Sonst → Frist beginnt im Folgemonat
 *  - Werktag = Mo-Fr, keine bundesweiten Feiertage (BGH VIII ZR 206/04)
 *
 * Prorate/Verkürzung ist ausdrücklich NICHT hier — dafür gibt es
 * TerminationEditDialog für nachträgliche Anpassungen.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

// ISO-fixed German holidays (bundesweit) – matches backend _GERMAN_FIXED_HOLIDAYS
const FIXED_HOLIDAYS = new Set(["01-01", "05-01", "10-03", "12-25", "12-26"]);

function isWorkday(iso) {
  const d = new Date(iso + "T00:00:00");
  const dow = d.getUTCDay();
  if (dow === 0 || dow === 6) return false;  // Sun, Sat
  const key = `${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
  if (FIXED_HOLIDAYS.has(key)) return false;
  return true;
}

function nthWorkdayOfMonth(year, month, n) {
  let d = new Date(Date.UTC(year, month - 1, 1));
  let count = 0;
  while (d.getUTCMonth() === month - 1) {
    const iso = d.toISOString().slice(0, 10);
    if (isWorkday(iso)) {
      count += 1;
      if (count === n) return iso;
    }
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return null;
}

function statutoryMonths(startIso, noticeBy, refIso) {
  if (noticeBy === "tenant") return 3;
  if (!startIso || !refIso) return 3;
  const years = (new Date(refIso) - new Date(startIso)) / (365.25 * 86_400_000);
  if (years >= 8) return 9;
  if (years >= 5) return 6;
  return 3;
}

function addMonthsToMonthEnd(iso, months) {
  const [y, m] = iso.split("-").map(Number);
  const end = new Date(Date.UTC(y, m - 1 + months + 1, 0));
  return end.toISOString().slice(0, 10);
}

/** Client-seitige Vorschau der Frist (identisch mit Backend-Logik) */
function calcEffective(receivedIso, noticeBy, contractStartIso) {
  if (!receivedIso) return { effective: "", meta: null };
  const [y, m] = receivedIso.split("-").map(Number);
  const thirdWd = nthWorkdayOfMonth(y, m, 3);
  const withinBonus = receivedIso <= thirdWd;
  let fristStart;
  if (withinBonus) {
    fristStart = `${y}-${String(m).padStart(2, "0")}-01`;
  } else {
    const next = new Date(Date.UTC(y, m, 1));
    fristStart = next.toISOString().slice(0, 10);
  }
  const months = statutoryMonths(contractStartIso, noticeBy, receivedIso);
  const effective = addMonthsToMonthEnd(fristStart, months - 1);
  return { effective, meta: { thirdWd, withinBonus, fristStart, months } };
}

export default function TerminateDialog({ open, onOpenChange, tenancy, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  // 2026-08 · Wenn bereits gekündigt → „Anpassen"-Modus (PATCH statt POST) mit
  // Vorbelegung der aktuellen Werte. History wird immer als Log angezeigt.
  const isPatchMode = tenancy?.termination_status === "cancelled" || tenancy?.termination_status === "ended";
  const [noticeBy, setNoticeBy] = useState("tenant");
  const [noticeDate, setNoticeDate] = useState(today);
  const [receivedAt, setReceivedAt] = useState(today);
  const [receivedBy, setReceivedBy] = useState("manager");
  const [customEnd, setCustomEnd] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const preview = useMemo(
    () => calcEffective(receivedAt, noticeBy, tenancy?.contract_start),
    [receivedAt, noticeBy, tenancy?.contract_start],
  );

  useEffect(() => {
    if (!customEnd) setEffectiveDate(preview.effective);
  }, [preview.effective, customEnd]);

  useEffect(() => {
    if (open) {
      if (isPatchMode) {
        // Aktuelle Kündigungs-Daten vorbelegen (aus tenancy)
        setNoticeBy(tenancy?.termination_notice_by || "tenant");
        setNoticeDate((tenancy?.termination_notice_date || today).slice(0, 10));
        setReceivedAt((tenancy?.termination_received_at || today).slice(0, 10));
        setReceivedBy(tenancy?.termination_received_by || "manager");
        setEffectiveDate((tenancy?.termination_effective_date || "").slice(0, 10));
        setCustomEnd(true);
        setReason(tenancy?.termination_reason || "");
      } else {
        setNoticeBy("tenant");
        setNoticeDate(today);
        setReceivedAt(today);
        setReceivedBy("manager");
        setCustomEnd(false);
        setEffectiveDate("");
        setReason("");
      }
      setComment("");
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tenancy?.id]);

  const submit = async () => {
    if (!effectiveDate) return toast.error("Bitte Vertragsende festlegen");
    setBusy(true);
    try {
      if (isPatchMode) {
        // Nur geänderte Felder patchen — Server hängt automatisch einen History-Eintrag an
        await api.patch(`/tenancies/${tenancy.id}/termination`, {
          notice_date: noticeDate !== (tenancy.termination_notice_date || "").slice(0, 10) ? noticeDate : null,
          received_at: receivedAt !== (tenancy.termination_received_at || "").slice(0, 10) ? receivedAt : null,
          received_by: receivedBy !== tenancy.termination_received_by ? receivedBy : null,
          effective_date: effectiveDate !== (tenancy.termination_effective_date || "").slice(0, 10) ? effectiveDate : null,
          reason: reason !== (tenancy.termination_reason || "") ? reason : null,
        });
        toast.success("Kündigung angepasst — Änderungen im Log protokolliert");
      } else {
        const { data } = await api.post(`/tenancies/${tenancy.id}/terminate`, {
          notice_by: noticeBy,
          notice_date: noticeDate,
          received_at: receivedAt,
          received_by: receivedBy,
          effective_date: customEnd ? effectiveDate : null,
          reason: reason || null,
        });
        let msg = `Gekündigt zum ${new Date(data.termination_effective_date).toLocaleDateString("de-DE")}`;
        if (data.deleted_future_postings) msg += ` · ${data.deleted_future_postings} Sollstellungen bereinigt`;
        toast.success(msg);
      }
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kündigung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const addComment = async () => {
    const text = comment.trim();
    if (!text) return;
    setBusy(true);
    try {
      await api.post(`/tenancies/${tenancy.id}/termination/comment`, { note: text });
      toast.success("Kommentar hinzugefügt");
      setComment("");
      onDone?.();  // parent reload holt neue History
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kommentar fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="terminate-dialog">
        <DialogHeader>
          <DialogTitle>
            {isPatchMode ? "Kündigung anpassen" : "Mietverhältnis kündigen"}
          </DialogTitle>
          {isPatchMode && (
            <div className="text-xs text-slate-500 mt-1">
              Alle Änderungen werden automatisch im Kündigungs-Log protokolliert.
            </div>
          )}
        </DialogHeader>

        {/* 2026-08 · Kündigungs-Log (immer sichtbar wenn History existiert) */}
        {(tenancy?.termination_history?.length || 0) > 0 && (
          <div className="border border-slate-200 rounded-md bg-slate-50 p-2 max-h-40 overflow-y-auto" data-testid="termination-log">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 px-1">
              Kündigungs-Logbuch ({tenancy.termination_history.length})
            </div>
            <ol className="space-y-1">
              {[...(tenancy.termination_history || [])].reverse().map((h, i) => (
                <li key={i} className="text-[11px] leading-snug bg-white border border-slate-200 rounded-sm px-2 py-1">
                  <div className="flex items-center gap-1.5">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                      h.action === "created" ? "bg-rose-500" :
                      h.action === "cancelled" ? "bg-amber-500" :
                      h.action === "patched" ? "bg-sky-500" :
                      h.action === "comment" ? "bg-slate-400" : "bg-slate-400"
                    }`} />
                    <span className="font-medium text-slate-700">
                      {h.action === "created" && "Kündigung erstellt"}
                      {h.action === "patched" && "Kündigung angepasst"}
                      {h.action === "cancelled" && "Kündigung zurückgenommen"}
                      {h.action === "comment" && "Kommentar"}
                    </span>
                    <span className="text-slate-400 tabular-nums ml-auto">
                      {h.at ? new Date(h.at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}
                    </span>
                  </div>
                  {h.note && <div className="text-slate-600 mt-0.5 pl-3">{h.note}</div>}
                  {h.changes && Object.keys(h.changes).length > 0 && (
                    <div className="text-slate-500 mt-0.5 pl-3 text-[10px]">
                      {Object.entries(h.changes).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                    </div>
                  )}
                </li>
              ))}
            </ol>
            {/* Kommentar-Feld */}
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addComment(); } }}
                placeholder="Kommentar zum Kündigungs-Log hinzufügen …"
                className="flex-1 h-8 text-xs px-2 border border-slate-300 rounded-sm bg-white"
                data-testid="termination-comment-input"
              />
              <Button size="sm" variant="outline" onClick={addComment}
                      disabled={busy || !comment.trim()}
                      data-testid="termination-comment-add">
                Notieren
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Kündigung durch</Label>
            <div className="mt-1 flex gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" checked={noticeBy === "tenant"} onChange={() => setNoticeBy("tenant")} disabled={isPatchMode} className="h-4 w-4 accent-slate-900" data-testid="terminate-by-tenant" /> Mieter
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" checked={noticeBy === "landlord"} onChange={() => setNoticeBy("landlord")} disabled={isPatchMode} className="h-4 w-4 accent-slate-900" data-testid="terminate-by-landlord" /> Vermieter
              </label>
              {isPatchMode && (
                <span className="text-[11px] text-slate-500 italic self-center">nicht änderbar</span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Kündigungsdatum</Label>
              <Input type="date" value={noticeDate} onChange={(e) => setNoticeDate(e.target.value)} data-testid="terminate-notice-date" />
              <div className="text-[11px] text-slate-500 mt-1">Datum auf dem Kündigungsschreiben</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Eingegangen am</Label>
              <Input type="date" value={receivedAt} onChange={(e) => setReceivedAt(e.target.value)} data-testid="terminate-received-at" />
              <div className="text-[11px] text-slate-500 mt-1">Treibt die Fristberechnung (Zugangsprinzip)</div>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Eingegangen bei</Label>
            <div className="mt-1 flex gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" checked={receivedBy === "manager"} onChange={() => setReceivedBy("manager")} className="h-4 w-4 accent-slate-900" data-testid="terminate-received-manager" /> Hausverwaltung
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" checked={receivedBy === "owner"} onChange={() => setReceivedBy("owner")} className="h-4 w-4 accent-slate-900" data-testid="terminate-received-owner" /> Eigentümer
              </label>
            </div>
          </div>

          {preview.meta && (
            <div className={`rounded-md border px-3 py-2.5 text-xs ${preview.meta.withinBonus ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-amber-200 bg-amber-50 text-amber-900"}`}>
              <div className="font-medium">
                {preview.meta.withinBonus
                  ? `✓ Zugang bis zum 3. Werktag (${new Date(preview.meta.thirdWd).toLocaleDateString("de-DE")}) — Frist läuft ab ${new Date(preview.meta.fristStart).toLocaleDateString("de-DE")}`
                  : `Zugang nach dem 3. Werktag (${new Date(preview.meta.thirdWd).toLocaleDateString("de-DE")}) — Fristbeginn ${new Date(preview.meta.fristStart).toLocaleDateString("de-DE")}`}
              </div>
              <div className="mt-0.5">
                {preview.meta.months} Monate Kündigungsfrist nach BGB §573c — regulär bis <strong>{new Date(preview.effective).toLocaleDateString("de-DE")}</strong>
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Vertragsende (effektiv)</Label>
            <div className="flex gap-2 items-center">
              <Input
                type="date" value={effectiveDate}
                onChange={(e) => { setEffectiveDate(e.target.value); setCustomEnd(true); }}
                data-testid="terminate-effective-date"
              />
              {customEnd && (
                <Button size="sm" variant="outline" onClick={() => { setCustomEnd(false); }}>
                  Zurücksetzen
                </Button>
              )}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              Wenn du ein früheres oder späteres Datum wählst, kannst du das später über „Kündigung ändern" mit anteiliger Berechnung noch anpassen.
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Notiz / Grund (optional)</Label>
            <textarea
              className="mt-1 w-full min-h-[60px] rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="z. B. Eigenbedarf · Zahlungsverzug · Umzug"
              data-testid="terminate-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Abbrechen</Button>
          <Button variant={isPatchMode ? "default" : "destructive"} onClick={submit} disabled={busy} data-testid="terminate-submit">
            {busy ? "Speichern…" : (isPatchMode ? "Änderungen speichern" : "Kündigen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
